import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { uuid } from '@deseretbook/utils'
import braintreeClient from 'braintree-web/client'
import braintreeHostedFields from 'braintree-web/hosted-fields'
import { Toasts, withTheme } from '@deseretbook/react-ui'
import { DeseretBook } from '../../dbcom/api'
import { Logger, formatErrorMessage } from '../../dbcom'

const logger = new Logger({ context: 'PaymentFormManager' })

/**
 * Generic form manager for managing Braintree hosted fields inside a form.
 */
class PaymentFormManager extends Component {
  constructor() {
    super()

    this.state = {
      // If currently communicating with the server.
      isFetchingData: true,
      // Braintree hosted fields data
      data: {
        cvv: {},
        number: {},
        expirationDate: {},
      },
    }

    // Generate unique ids for this payment form.
    this.cardNumberId = uuid('number')
    this.cvvId = uuid('cvv')
    this.expirationId = uuid('expiration')

    // Instance variables of Braintree things
    this.clientToken = null
    this.hostedFields = {}

    // Instance methods
    this.getBraintreeClientToken = this.getBraintreeClientToken.bind(this)
    this.initializeBraintreeHostedFields = this.initializeBraintreeHostedFields.bind(this)
    this.updateHostedFields = this.updateHostedFields.bind(this)
  }

  /**
   * Initialize Braintree fields on mount.
   */
  componentDidMount() {
    this.getBraintreeClientToken()
  }

  /**
   * Gets a client payment token to be used for the payment method. Once a token
   * is received we can initialize the connection with Braintree.
   */
  async getBraintreeClientToken() {
    const { paymentMethod } = this.props

    // TODO We should have some sort of error state for the form so people know
    // they can't process new cards.
    if (!paymentMethod) {
      Toasts.danger('Unable to process credit cards', 'We are unable to process credit cards at this time')
      logger.critical('Unable to process credit cards. No payment method found.')

      return
    }

    try {
      const { clientToken } = await DeseretBook
        .order()
        .payment()
        .clientToken({
          paymentMethodId: paymentMethod.id,
        })

      this.clientToken = clientToken

      this.initializeBraintreeHostedFields()
    } catch ({ message, errors }) {
      logger.critical(`Unable to get client token from Braintree: "${formatErrorMessage(message, errors)}"`, { errors })
      Toasts.danger('Unable to get token', formatErrorMessage(message, errors))
    }
  }

  /**
   * Initializes hosted fields with Braintree.
   */
  async initializeBraintreeHostedFields() {
    const { theme } = this.props

    braintreeClient
      .create({
        authorization: this.clientToken,
      })
      .then(client => braintreeHostedFields
        .create({
          client,
          // Make the inputs look as close to the real inputs as possible.
          styles: {
            input: {
              'font-family': 'Arial, Geneva, sans-serif',
              color: theme.typography.dark.main,
              'font-size': theme.typography.base.fontSize,
              'line-height': theme.typography.base.lineHeight,
              padding: theme.spacing.use(0.5, 2.5, 0.5, 0.5),
              outline: theme.typography.dark.main,
              width: '100%',
              background: theme.palette.background.main,
              // TODO use theme variable
              borderRadius: '3px',
            },
            'input.invalid': {
              color: theme.palette.danger.main,
              outline: theme.palette.danger.dark,
            },
          },
          fields: {
            number: {
              selector: `#${this.cardNumberId}`,
            },
            cvv: {
              selector: `#${this.cvvId}`,
            },
            expirationDate: {
              selector: `#${this.expirationId}`,
            },
          },
        }))
      .then((hostedFields) => {
        this.hostedFields = hostedFields

        this.hostedFields.on('focus', this.updateHostedFields)
        this.hostedFields.on('blur', this.updateHostedFields)
        this.hostedFields.on('empty', this.updateHostedFields)
        this.hostedFields.on('notEmpty', this.updateHostedFields)
        this.hostedFields.on('validityChange', this.updateHostedFields)
        this.hostedFields.on('inputSubmitRequest', this.updateHostedFields)
        this.hostedFields.on('cardTypeChange', ({ cards }) => {
          this.setState({
            currentCard: cards.length === 1
              ? cards[0]
              : null,
          })
        })

        this.setState({ isFetchingData: false })
      })
      .catch(({ message, errors }) => {
        logger.critical(`Unable to setup payment: "${formatErrorMessage(message, errors)}"`, { errors })
        Toasts.danger('Unable to setup payment', formatErrorMessage(message, errors))
        this.setState({ isFetchingData: false })
      })
  }

  /**
   * Callback to allow Braintree to notify us of changes to the hosted fields'
   * state. This allows us to style our inputs properly.
   */
  updateHostedFields({ fields }) {
    this.setState({ fields })
  }

  render() {
    const { children } = this.props
    const { fields, isFetchingData, currentCard } = this.state

    return children({
      fields,
      isFetchingData,
      hostedFields: this.hostedFields,
      cardNumberId: this.cardNumberId,
      cvvId: this.cvvId,
      expirationId: this.expirationId,
      currentCard,
    })
  }
}

PaymentFormManager.propTypes = {
  children: PropTypes.func.isRequired,
  // Current address data
  address: PropTypes.object,
  // Current theme instance (private) used for styling hosted inputs.
  theme: PropTypes.object,
  // Callback for when input value changes.
  onChange: PropTypes.func,
  /** Payment Method to use. */
  paymentMethod: PropTypes.object,
}

PaymentFormManager.defaultProps = {
  address: {},
  theme: {},
  onChange: () => {},
  paymentMethod: null,
}

export default withTheme(PaymentFormManager)
